import { useDebounce } from '@uidotdev/usehooks';
import { useCallback, useEffect, useState } from 'react';

import getCustomers from '@/shared/api/customers/getCustomers';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Customer from '@/shared/types/customer.type';
import DialogName from '@/shared/types/dialogName.enum';
import getSearchBodyFilters from '@/shared/utils/getSearchBodyFilters';

import UpdateHandlers from '../../../../types/updateHandlers.type';

type Args = UpdateHandlers<unknown>;

const useHandleAssignCustomer = ({ onUpdateDialogState }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [searchCustomerValue, setSearchCustomerValue] = useState('');
  const searchCustomerValueDebounced = useDebounce(searchCustomerValue, 500);

  const [assignedCustomer, setAssignedCustomer] = useState<Customer | null>(
    null,
  );
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  const [isCustomModal, setIsCustomModal] = useState(false);

  const fetchCustomers = useCallback(
    async (value?: string) => {
      setCustomers([]);

      const searchableFields: (keyof Customer)[] = [
        'billing_address',
        'billing_address2',
        'billing_address_type',
        'billing_city',
        'billing_country',
        'billing_postcode',
        'billing_state',
        'delivery_address',
        'delivery_address2',
        'delivery_address_type',
        'delivery_city',
        'delivery_country',
        'delivery_postcode',
        'delivery_state',
        'email_address',
        'first_name',
        'last_name',
        'phone_number',
        'in_customer_id',
      ];

      if (!isCustomModal) {
        onUpdateDialogState(DialogName.AssignCustomer, {
          open: true,
          loading: true,
        });
      }

      const response = await getCustomers(
        {
          search: value
            ? getSearchBodyFilters({
                mode: 'oneValueMultipleKeys',
                keys: searchableFields,
                value,
              })
            : [],
        },
        redirectLogin,
      );

      if (response.success) {
        setCustomers(response.data);

        onUpdateDialogState(DialogName.AssignCustomer, { loading: false });
      } else {
        onUpdateDialogState(DialogName.AssignCustomer, { loading: false });
      }
    },
    [onUpdateDialogState, redirectLogin, isCustomModal],
  );

  const handleAssignCustomer = (customModal?: boolean) => {
    if (customModal) {
      setIsCustomModal(customModal);
    }

    setShouldFetch(true);
  };

  const selectCustomer = (customerId: string) => {
    const selectedCustomer = customers?.find(
      (item) => item.in_customer_id === customerId,
    );

    setAssignedCustomer(selectedCustomer || null);
  };

  const searchCustomer = (value: string) => {
    setSearchCustomerValue(value);
  };

  useEffect(() => {
    if (searchCustomerValueDebounced) {
      void fetchCustomers(searchCustomerValueDebounced);
    }
  }, [fetchCustomers, onUpdateDialogState, searchCustomerValueDebounced]);

  useEffect(() => {
    if (shouldFetch) {
      void fetchCustomers();
      setShouldFetch(false);
    }
  }, [
    fetchCustomers,
    onUpdateDialogState,
    shouldFetch,
    searchCustomerValueDebounced,
  ]);

  return {
    trigger: handleAssignCustomer,
    searchBoxProps: {
      customers,
      searchCustomerValue,
      assignedCustomer,
      selectCustomer,
      searchCustomer,
    },
  };
};

export default useHandleAssignCustomer;
